// CSR-2095 — compact multi-select for a table-header filter cell.
//
// Exists because the Stale Sync triage question is "show me everything that
// needs action" — Failed OR Not found OR "--" — and a text input cannot
// express OR. Used only on columns whose values are a closed vocabulary
// (Status, Last outcome); open-ended columns like Client stay text inputs,
// where typing a fragment beats picking from a list.
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ColumnOption {
  value: string;
  /** How many loaded rows carry this value — shown so the picker doubles as a
   *  distribution view: you see where the problems are before filtering. */
  count: number;
}

interface Props {
  options: ColumnOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  /** Column name, used for the empty-state label and the accessible name. */
  label: string;
  testId: string;
}

export function ColumnMultiSelect({
  options,
  selected,
  onChange,
  disabled,
  label,
  testId,
}: Props) {
  function toggle(value: string) {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  }

  // Selecting nothing means no filtering — the same as an empty text input, so
  // the two filter kinds behave alike from the user's side.
  const summary =
    selected.length === 0
      ? "Filter…"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <Popover>
      <PopoverTrigger
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        className={cn(
          "flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input",
          "bg-transparent px-3 text-xs font-normal shadow-sm",
          disabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer hover:bg-muted/50",
          selected.length === 0 && "text-muted-foreground",
        )}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        {options.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">
            No values to filter.
          </p>
        ) : (
          <>
            {options.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggle(option.value)}
                  data-testid={`${testId}-option-${option.value}`}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
                >
                  <Check
                    className={cn(
                      "h-3 w-3 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 truncate">{option.value}</span>
                  <span className="text-muted-foreground">{option.count}</span>
                </button>
              );
            })}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                data-testid={`${testId}-clear`}
                className="mt-1 w-full cursor-pointer rounded-sm border-t px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted"
              >
                Clear
              </button>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
